import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Deal, DealStage, DealType, DealPriority, PaymentScheduleItem, ExclusivityCategory, DeliverableItem } from '../types'
import { handleApiError } from '../utils/apiErrorHandler'
import { ensureUserAgency } from '../utils/ensureAgency'
import { logger } from '../utils/logger'
import { autoRecordDealCommission } from '../utils/commissionAutoRecord'
import { cascadeDelete, fieldFilter, entityFilter } from '../utils/cascadeDeleteHelper'
import { guardedUpdate } from '../utils/optimisticLock'
import { notifyAthlete } from './portalNotify'
import { createPlayerTransaction } from './playerTransactions'

// Re-export centralized pipeline calculation functions
// These are the SINGLE SOURCE OF TRUTH - do not duplicate this logic
export {
  calculateWeightedPipelineValue,
  getDealProbability,
  isActiveDeal,
  isWonDeal,
  isLostDeal,
  calculateTotalPipelineValue,
  calculatePipelineByStage,
  calculateCommissionPipeline,
  calculateWinRate,
  calculateAverageDealValue,
  calculateProjectedEarnings,
  getDealsSummary,
} from '../utils/pipelineCalculations'

// Re-export stage constants for convenience
export {
  STAGE_PROBABILITIES,
  STAGE_ORDER,
  ACTIVE_STAGES,
  TERMINAL_STAGES,
  WON_STAGE,
  LOST_STAGE,
  STAGE_COLORS,
  STAGE_LABELS,
} from '../constants/dealStages'

function mapDeal(record: Record<string, any>): Deal {
  return {
    id: record.id,
    title: record.title ?? '',
    description: record.description ?? null,
    deal_type: record.deal_type ?? 'other',
    stage: record.stage ?? 'Prospect',
    probability: record.probability ?? null,
    value: record.value ?? null,
    commission_rate: record.commission_rate ?? null,
    commission_amount: record.commission_amount ?? null,
    payment_schedule: Array.isArray(record.payment_schedule) ? record.payment_schedule : null,
    show_value_to_client: record.show_value_to_client === true,
    deliverables: Array.isArray(record.deliverables) ? record.deliverables : null,
    exclusivity_category: record.exclusivity_category ?? null,
    // Relations
    client_id: record.client_id ?? '',
    organization_id: record.organization_id ?? null,
    contact_id: record.contact_id ?? null,
    // Dates
    expected_close: record.expected_close ?? null,
    actual_close_date: record.actual_close_date ?? null,
    contract_start_date: record.contract_start_date ?? null,
    contract_end_date: record.contract_end_date ?? null,
    // Additional fields
    source: record.source ?? null,
    lost_reason: record.lost_reason ?? null,
    priority: record.priority ?? null,
    tags: Array.isArray(record.tags) ? record.tags : null,
    notes: record.notes ?? null,
    // Agency and ownership
    agency_id: record.agency_id ?? '',
    user_id: record.user_id ?? null,
    assigned_agent_id: record.assigned_agent_id ?? null,
    // Timestamps
    stage_changed_at: record.stage_changed_at ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
    // Audit trail
    updated_by: record.updated_by ?? null,
    // Legacy aliases for backward compatibility
    owner_id: record.user_id ?? record.owner_id ?? '',
    amount: record.value ?? null,
    status: record.stage ?? 'Prospect',
  }
}

export type DealPayload = {
  title: string // Required
  deal_type: DealType // Required
  stage: DealStage // Required
  client_id: string // Required
  description?: string | null
  probability?: number | null
  value?: number | null
  commission_rate?: number | null
  commission_amount?: number | null
  payment_schedule?: PaymentScheduleItem[] | null
  show_value_to_client?: boolean
  deliverables?: DeliverableItem[] | null
  exclusivity_category?: ExclusivityCategory | null
  organization_id?: string | null
  contact_id?: string | null
  expected_close?: string | null
  actual_close_date?: string | null
  contract_start_date?: string | null
  contract_end_date?: string | null
  source?: string | null
  lost_reason?: string | null
  priority?: DealPriority | null
  tags?: string[] | null
  notes?: string | null
  agency_id?: string
  user_id?: string | null
}

export type PaginatedDealsResult = {
  items: Deal[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}


export async function listDeals(options?: { expand?: string; limit?: number; client_id?: string; filter?: string }) {
  
  // Get current user's agency for filtering
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  const userId = user?.id
  const isSuperAdmin = (user as any)?.role === 'superadmin'

  // If no agency and not super admin and no user id, return empty
  if (!agencyId && !isSuperAdmin && !userId) {
    return []
  }

  // Build filter - super admins see all (unless external filter provided),
  // regular users see their agency OR their deals
  const filterConditions: string[] = []
  if (!isSuperAdmin) {
    const authConditions: string[] = []
    if (agencyId) {
      authConditions.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
    }
    if (userId) {
      authConditions.push(`user_id = "${escapeFilterValue(userId)}"`)
    }
    if (authConditions.length > 0) {
      filterConditions.push(`(${authConditions.join(' || ')})`)
    }
  }

  // Add external filter (e.g. work view filter)
  if (options?.filter) {
    filterConditions.push(`(${options.filter})`)
  }

  // Add client_id filter if provided
  if (options?.client_id) {
    filterConditions.push(`client_id = "${escapeFilterValue(options.client_id)}"`)
  }

  const queryOptions: { filter?: string; sort: string; expand?: string } = {
    sort: '-updated'
  }
  if (filterConditions.length > 0) {
    queryOptions.filter = filterConditions.join(' && ')
  }

  if (options?.expand) {
    queryOptions.expand = options.expand
  }

  // Use pagination if limit is specified, otherwise fetch all
  let data: Record<string, any>[]
  if (options?.limit) {
    const result = await pb.collection('deals').getList<Record<string, any>>(1, options.limit, queryOptions)
    data = result.items
  } else {
    data = await pb.collection('deals').getFullList<Record<string, any>>(queryOptions)
  }

  return data.map(record => ({
    ...mapDeal(record),
    expand: record.expand || null
  }))
}

export async function listDealsPaginated(
  page: number = 1,
  perPage: number = 25,
  options?: { sort?: string; filter?: string }
): Promise<PaginatedDealsResult> {
  
  // Get current user's agency for filtering
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  const userId = user?.id
  const isSuperAdmin = (user as any)?.role === 'superadmin'

  // If no agency and not super admin and no user id, return empty
  if (!agencyId && !isSuperAdmin && !userId) {
    return { items: [], page: 1, perPage, totalItems: 0, totalPages: 0 }
  }

  // Build filter - super admins see all, regular users see their agency OR their deals
  let baseFilter = ''
  if (!isSuperAdmin) {
    const conditions: string[] = []
    if (agencyId) {
      conditions.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
    }
    if (userId) {
      conditions.push(`user_id = "${escapeFilterValue(userId)}"`)
    }
    baseFilter = conditions.length > 0 ? `(${conditions.join(' || ')})` : ''
  }

  // Combine with user's additional filter
  let filter = baseFilter
  if (options?.filter) {
    filter = baseFilter ? `(${baseFilter}) && (${options.filter})` : options.filter
  }

  const result = await pb.collection('deals').getList<Record<string, any>>(page, perPage, {
    sort: options?.sort ?? '-updated',
    filter,
  })
  return {
    items: result.items.map(mapDeal),
    page: result.page,
    perPage: result.perPage,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

export async function getDeal(id: string) {
  const record = await pb.collection('deals').getOne<Record<string, any>>(id)
  return mapDeal(record)
}

export async function createDeal(payload: DealPayload, userId: string) {
  const userAgencyId = await ensureUserAgency()
  const createPayload: any = { ...payload, agency_id: userAgencyId }
  if (userId) {
    createPayload.user_id = userId
  }
  Object.keys(createPayload).forEach(key => {
    if (createPayload[key] === undefined || createPayload[key] === null || createPayload[key] === '') {
      delete createPayload[key]
    }
  })
  try {
    const record = await pb.collection('deals').create(createPayload)
    return mapDeal(record)
  } catch (err: any) {
    const apiError = handleApiError(err, 'Deal creation')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    ;(errorToThrow as any).fieldErrors = apiError.fieldErrors
    throw errorToThrow
  }
}

export async function updateDeal(id: string, payload: Partial<DealPayload>, lastUpdated?: string) {
  try {
    // Fetch previous state for automation triggers and stage tracking
    let previousDeal: Deal | null = null
    try {
      previousDeal = await getDeal(id)
    } catch {
      // Continue without previous state — automation triggers are best-effort
    }

    // Build update payload, tracking stage changes
    const updatePayload: Record<string, any> = { ...payload }

    // Set updated_by for audit trail
    const user = pb.authStore.model
    if (user?.id) {
      updatePayload.updated_by = user.id
    }

    // Only update stage_changed_at when stage actually changes
    if (payload.stage && previousDeal && payload.stage !== previousDeal.stage) {
      updatePayload.stage_changed_at = new Date().toISOString()

      // Auto-record commission when deal moves to Executed
      if (payload.stage === 'Executed' && previousDeal.stage !== 'Executed') {
        updatePayload.actual_close_date = new Date().toISOString().split('T')[0]
      }
    }

    const record = await guardedUpdate('deals', id, updatePayload, lastUpdated)
    const updatedDeal = mapDeal(record)

    // Commission follows paid money. Execution books it; a change to the
    // payment schedule, the rate or the value on an executed deal re-prices it.
    const executedNow = payload.stage === 'Executed' && previousDeal && previousDeal.stage !== 'Executed'
    const moneyTouched = ['payment_schedule', 'commission_rate', 'value'].some(k => k in payload)
    if (!executedNow && updatedDeal.stage === 'Executed' && moneyTouched) {
      autoRecordDealCommission(updatedDeal).catch(err => {
        logger.error('Commission reconcile failed for deal', updatedDeal.id, err)
      })
    }
    if (executedNow) {
      autoRecordDealCommission(updatedDeal).catch(err => {
        logger.error('Commission auto-record failed for deal', updatedDeal.id, err)
      })
      // An executed NIL deal is a transaction in the athlete's history, next
      // to their roster moves, not only a row on the pipeline.
      if (isNilDeal(updatedDeal) && updatedDeal.client_id) {
        recordNilTransaction(updatedDeal).catch(err => {
          logger.error('NIL transaction record failed for deal', updatedDeal.id, err)
        })
      }
    }


    // Push notification to athlete when deal stage changes
    if (payload.stage && previousDeal && payload.stage !== previousDeal.stage) {
      notifyAthleteOfStageChange(updatedDeal, payload.stage, previousDeal.stage).catch(err => {
        logger.error('Athlete notification failed for deal', updatedDeal.id, err)
      })
    }

    return updatedDeal
  } catch (err: any) {
    const apiError = handleApiError(err, 'Update deal')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    throw errorToThrow
  }
}

export async function deleteDeal(id: string) {
  return deleteDealCascade(id)
}

/**
 * Delete deal and related records (best-effort cascade).
 * Fetches all related collections in parallel, deletes in batched parallel.
 */
export async function deleteDealCascade(id: string) {
  try {
    logger.info('Starting cascade delete for deal:', id)
    const eid = escapeFilterValue(id)

    // Parent first, so a caller the rules refuse changes nothing. A failure
    // in the cascade below is reported, not swallowed: an invoice left
    // pointing at a deleted deal is something the user needs to know about.
    await pb.collection('deals').delete(id)

    await cascadeDelete([
      { collection: 'commission_ledger',            filter: fieldFilter('deal_id', id) },
      { collection: 'deal_offers',                  filter: fieldFilter('deal_id', id) },
      { collection: 'negotiation_scenarios',         filter: fieldFilter('deal_id', id) },
      { collection: 'negotiation_leverage_points',   filter: fieldFilter('deal_id', id) },
      { collection: 'invoices',                      filter: fieldFilter('deal_id', id) },
      { collection: 'transactions',                  filter: fieldFilter('deal_id', id) },
      { collection: 'communications',                filter: fieldFilter('deal_id', id) },
      { collection: 'documents',                     filter: fieldFilter('deal_id', id) },
      { collection: 'tasks',                         filter: `deal_id = "${eid}" || (entity_type = "deal" && entity_id = "${eid}")` },
      { collection: 'notes',                         filter: `deal = "${eid}" || (related_entity_type = "deal" && related_entity_id = "${eid}")` },
      { collection: 'activities',                    filter: fieldFilter('deal_id', id) },
      { collection: 'calendar_events',               filter: `deal_id = "${eid}" || (related_entity_type = "deal" && related_entity_id = "${eid}")` },
      { collection: 'notifications',                 filter: entityFilter('related_entity_type', 'deal', 'related_entity_id', id) },
      { collection: 'contracts',                     filter: fieldFilter('deal_id', id) },
    ])
    logger.info('Deal deleted successfully')
  } catch (err: any) {
    const apiError = handleApiError(err, 'Delete deal cascade')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    throw errorToThrow
  }
}

export function groupDealsByStage(deals: Deal[]) {
  return deals.reduce<Record<DealStage, Deal[]>>(
    (acc, deal) => {
      const stage = deal.stage ?? 'Prospect'
      if (acc[stage]) {
        acc[stage] = [...acc[stage], deal]
      }
      return acc
    },
    {
      Prospect: [],
      Outreach: [],
      Negotiating: [],
      Agreed: [],
      'Contract Sent': [],
      Executed: [],
      Lost: [],
    },
  )
}

/**
 * List deals for a specific client
 * More efficient than loading all deals and filtering client-side
 */
export async function listClientDeals(clientId: string, options?: { expand?: string; limit?: number }) {
  return listDeals({ client_id: clientId, ...options })
}

/**
 * Check if a client already has an active deal in the same exclusivity category.
 * Returns the conflicting deals (if any). Does not block — caller decides how to warn.
 */
export async function checkExclusivityConflict(
  clientId: string,
  category: ExclusivityCategory,
  excludeDealId?: string,
): Promise<Deal[]> {
  const cid = escapeFilterValue(clientId)
  const cat = escapeFilterValue(category)
  // Conflict = any non-Lost deal in this exclusivity category.
  // Executed deals intentionally COUNT as conflicts: an executed exclusive deal is a
  // binding contract, so a second deal in the same category is exactly what we want to
  // flag. Only Lost deals are dead and therefore excluded.
  let filter = `client_id = "${cid}" && exclusivity_category = "${cat}" && stage != "Lost"`
  if (excludeDealId) {
    filter += ` && id != "${escapeFilterValue(excludeDealId)}"`
  }
  const records = await pb.collection('deals').getFullList<Record<string, any>>({ filter })
  return records.map(mapDeal)
}

async function notifyAthleteOfStageChange(deal: Deal, newStage: string, _prevStage: string): Promise<void> {
  const stageLabels: Record<string, string> = {
    Negotiating: 'Actively Negotiating',
    Agreed: 'Terms Agreed',
    Executed: 'Deal Executed 🏆',
    Lost: 'Deal Closed',
  }

  const label = stageLabels[newStage] || newStage
  const body = newStage === 'Executed'
    ? `Your deal "${deal.title}" is official. Check your portal for details.`
    : `Your agent updated "${deal.title}" to ${label}. Check your portal.`

  // Server-side: /api/portal/notify checks the client is in the caller's
  // agency and holds the notification secret. Errors propagate to the
  // caller in updateDeal, which logs them without failing the stage change.
  await notifyAthlete('deal_update', deal.client_id, {
    deal_id: deal.id,
    deal_title: deal.title,
    new_stage: label,
    message: body,
  })
}

/**
 * Fetch closed/lost deals with the same organization — powers DealMemoryPanel so agents
 * can see what worked and what didn't the last time they negotiated with this party.
 */
export async function getPastDealsWithOrg(
  orgId: string,
  excludeDealId?: string,
): Promise<Deal[]> {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (!agencyId || !orgId) return []

  const filterParts = [
    `organization_id = "${escapeFilterValue(orgId)}"`,
    `agency_id = "${escapeFilterValue(agencyId)}"`,
    `(stage = "Executed" || stage = "Lost")`,
  ]
  if (excludeDealId) {
    filterParts.push(`id != "${escapeFilterValue(excludeDealId)}"`)
  }

  try {
    const records = await pb.collection('deals').getList<Record<string, any>>(1, 8, {
      filter: filterParts.join(' && '),
      sort: '-actual_close_date,-created',
      fields: 'id,title,stage,value,deal_type,actual_close_date,contract_start_date,contract_end_date,lost_reason,notes,created',
    })
    return records.items.map(mapDeal)
  } catch {
    return []
  }
}

/** Both kinds of NIL agreement: marketing (name, image, likeness) and a collective's revenue share. */
export function isNilDeal(deal: Pick<Deal, 'deal_type'>): boolean {
  return deal.deal_type === 'nil' || deal.deal_type === 'nil_rev_share'
}

/**
 * An executed NIL deal, written to the athlete's transaction history so it
 * sits beside their roster moves. Keyed on the deal id so a stage bounced
 * back and forth writes one row.
 */
async function recordNilTransaction(deal: Deal): Promise<void> {
  if (!deal.client_id) return
  const existing = await pb.collection('player_transactions').getList(1, 1, {
    filter: `espn_transaction_id = "${escapeFilterValue(`deal:${deal.id}`)}"`,
  })
  if (existing.totalItems > 0) return
  const client = await pb.collection('clients').getOne(deal.client_id, { fields: 'id,full_name' })
  await createPlayerTransaction({
    player_name: client.full_name,
    transaction_type: 'nil_deal',
    transaction_date: deal.actual_close_date || new Date().toISOString().slice(0, 10),
    to_team: '',
    from_team: '',
    contract_total: deal.value ?? null,
    league: 'College',
    source: 'app',
    is_agency_client: true,
    client_id: deal.client_id,
    espn_transaction_id: `deal:${deal.id}`,
    match_confidence: 'manual',
    notes: deal.deal_type === 'nil_rev_share' ? `${deal.title} (revenue share)` : deal.title,
  } as Parameters<typeof createPlayerTransaction>[0])
}
